import {
  captureException,
  captureMessage,
  withScope,
} from "@client/telemetry/sentry";

/**
 * Report web-worker failures to Sentry. Workers run outside the page's Sentry
 * instrumentation, so without this their crashes are invisible in production —
 * search silently stops working and the only trace is a console line nobody is
 * watching.
 *
 * Two layers, matching the two ways a worker fails:
 *  - `attachWorkerErrorReporting` covers failures the worker can't report
 *    itself: a script that fails to load/parse, an uncaught throw outside its
 *    message handling, or a structured-clone failure (`messageerror`).
 *  - `reportWorkerProtocolError` is for in-protocol `{ type: "error" }`
 *    messages a worker posts from its own catch block; the host calls it from
 *    its existing error branch, alongside its recovery logic.
 *
 * When the worker forwarded a real stack, it is grafted onto the captured
 * exception (Sentry parses the `.stack` string at capture time), so grouping
 * and frame navigation point at the failing worker code. Without one, fall
 * back to captureMessage — a synthesized Error would carry THIS file's stack
 * and misgroup every distinct worker failure into one issue about the helper.
 */
// A corrupted index can fail EVERY search — one report per keystroke. Cap
// reports per worker per page load: enough to see the failure and its shape
// in Sentry, without a broken session turning into an event flood. (Sentry
// groups same-message events into one issue either way; this bounds volume.)
const MAX_REPORTS_PER_WORKER = 5;
const reportCounts = new Map<string, number>();

function shouldReport(name: string): boolean {
  const count = reportCounts.get(name) ?? 0;

  if (count >= MAX_REPORTS_PER_WORKER) {
    return false;
  }

  reportCounts.set(name, count + 1);
  return true;
}

function captureWithWorkerStack(message: string, stack: string): void {
  const error = new Error(message);
  error.name = "WorkerError";
  // V8 stacks open with an "Error: message" header line; WebKit/Firefox
  // stacks start directly at the throwing frame ("fn@url:line:col"). Drop the
  // first line only when it is a header — stripping unconditionally would
  // delete the actual throwing frame on the iPad's WebKit.
  const [firstLine = "", ...restLines] = stack.split("\n");
  const firstLineIsFrame =
    /^\s*at\s/.test(firstLine) || firstLine.includes("@");
  const frameLines = firstLineIsFrame ? [firstLine, ...restLines] : restLines;
  // Sentry derives the event stacktrace by parsing this string — replacing it
  // makes the event's frames (and frame-based grouping) the worker's own.
  error.stack = `${error.name}: ${message}\n${frameLines.join("\n")}`;
  captureException(error);
}

export function attachWorkerErrorReporting(worker: Worker, name: string): void {
  worker.onerror = (event) => {
    if (!shouldReport(name)) {
      return;
    }

    withScope((scope) => {
      scope.setContext("worker", {
        name,
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });

      // Worker ErrorEvents surface the underlying Error only same-thread;
      // cross-thread `event.error` is typically null. Use it when present.
      if (event.error instanceof Error) {
        captureException(event.error);
      } else {
        captureMessage(`${name} worker crashed: ${event.message}`, "error");
      }
    });
  };

  worker.onmessageerror = () => {
    if (!shouldReport(name)) {
      return;
    }

    withScope((scope) => {
      scope.setContext("worker", { name });
      captureMessage(`${name} worker message failed to deserialize`, "error");
    });
  };
}

export function reportWorkerProtocolError(
  name: string,
  message: string,
  stack?: string,
): void {
  if (!shouldReport(name)) {
    return;
  }

  withScope((scope) => {
    scope.setContext("worker", { name, message });

    if (stack) {
      captureWithWorkerStack(`${name} worker error: ${message}`, stack);
    } else {
      captureMessage(`${name} worker error: ${message}`, "error");
    }
  });
}
